import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { User, Unit, Event, Group } from '../types';
import { getEventDescription, getEventUserDisplay } from './eventUtils';

export const exportToExcel = (data: any[], filename: string, sheetName: string = 'Data') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  // Adjust column widths
  const colWidths = Object.keys(data[0] || {}).map(() => ({ wch: 20 }));
  worksheet['!cols'] = colWidths;
  
  XLSX.writeFile(workbook, `${filename}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`);
};

export const exportUserData = (user: User) => {
  const userData = [{
    'ID': user.id,
    'Name': user.name,
    'Email': user.email,
    'Export date': format(new Date(), 'dd/MM/yyyy HH:mm:ss', { locale: enUS })
  }];
  
  exportToExcel(userData, 'user_data', 'User');
};

export const exportUnitsData = (units: Unit[]) => {
  const unitsData = units.map(unit => ({
    'ID': unit.id,
    'Name': unit.name,
    'Description': unit.description || 'N/A',
    'Group': unit.group_name || 'No group',
    'Shared users': unit.sharedWith?.map(u => u.email).join(', ') || 'None',
    'Created date': format(new Date(unit.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: enUS }),
    'Last updated': format(new Date(unit.updated_at), 'dd/MM/yyyy HH:mm:ss', { locale: enUS })
  }));

  exportToExcel(unitsData, 'units', 'Units');
};


export const exportEventsData = (events: Event[], unitName?: string) => {
  const dataToExport = events.map(event => ({
    'Event ID': event.id,
    'Unit ID': event.unit_id,
    'Event': getEventDescription(event),
    'User': getEventUserDisplay(event),
    'Date and time': format(new Date(event.registered_at), 'dd/MM/yyyy HH:mm', { locale: enUS }),
  }));

  const filename = unitName ? `events-${unitName.replace(/ /g, '_')}` : `events-${new Date().toISOString()}`;
  exportToExcel(dataToExport, filename, 'Events');
};

export const exportGroupsData = (groups: Group[]) => {
  const groupsData = groups.map(group => ({
    'ID': group.id,
    'Name': group.name,
    'Description': group.description || '',
    'Units number': group.units.length,
    'Units': group.units.map(u => u.name).join(', ')
  }));
  
  exportToExcel(groupsData, 'groups', 'Groups');
};

export const exportAllData = (user: User, units: Unit[], events: Event[], groups: Group[]) => {
  const workbook = XLSX.utils.book_new();

  // User sheet
  const userData = [{
    'ID': user.id,
    'Name': user.name,
    'Email': user.email,
    'Export date': format(new Date(), 'dd/MM/yyyy HH:mm:ss', { locale: enUS })
  }];
  const userSheet = XLSX.utils.json_to_sheet(userData);
  XLSX.utils.book_append_sheet(workbook, userSheet, 'User');

  // Units sheet
  const unitsData = units.map(unit => ({
    'ID': unit.id,
    'Name': unit.name,
    'Description': unit.description || 'N/A',
    'Group': unit.group_name || 'No group',
    'Shared users': unit.sharedWith?.map(u => u.email).join(', ') || 'None',
    'Creation date': format(new Date(unit.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: enUS }),
    'Last updated': format(new Date(unit.updated_at), 'dd/MM/yyyy HH:mm:ss', { locale: enUS })
  }));
  const unitsSheet = XLSX.utils.json_to_sheet(unitsData);
  XLSX.utils.book_append_sheet(workbook, unitsSheet, 'Units');

  // Events sheet
  const eventsData = events.map(event => ({
    'ID': event.id,
    'Unit ID': event.unit_id,
    'Type': event.type,
    'Description': getEventDescription(event),
    'User': getEventUserDisplay(event),
    'Date and time': format(new Date(event.registered_at), 'dd/MM/yyyy HH:mm:ss', { locale: enUS }),
  }));
  const eventsSheet = XLSX.utils.json_to_sheet(eventsData);
  XLSX.utils.book_append_sheet(workbook, eventsSheet, 'Events');

  // Groups sheet
  const groupsData = groups.map(group => ({
    'ID': group.id,
    'Name': group.name,
    'Description': group.description || '',
    'Units number': group.units.length,
    'Units': group.units.map(u => u.name).join(', ')
  }));
  const groupsSheet = XLSX.utils.json_to_sheet(groupsData);
  XLSX.utils.book_append_sheet(workbook, groupsSheet, 'Groups');

  XLSX.writeFile(workbook, `complete_data_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`);
};